import { Id, ToastOptions, toast as toastify, ToastContentProps } from "react-toastify";

import Icons from "assets";

/** Requires *at least* one of `title` or `message` */
type ToastMessaging =
  | {
      title: string;
      message?: string;
    }
  | {
      title?: string;
      message: string;
    };

export type ToastProps = {
  data: ToastMessaging;
} & React.PropsWithChildren;

export const Toast = ({ data: { title, message }, children }: ToastProps) => {
  return (
    <div className="space-y-3 pl-2">
      <div className="flex flex-col gap-y-1">
        {title && <p className="text-gray-900 font-bold leading-6">{title}</p>}
        {message && <p className="text-gray-600 text-sm leading-[22px]">{message}</p>}
      </div>
      {children && <div>{children}</div>}
    </div>
  );
};

type CustomToastOptions = Omit<ToastOptions, "render"> & {
  data: ToastMessaging;
  render?: (props: ToastContentProps<ToastMessaging>) => React.ReactNode;
};

type ToastService = {
  (options: CustomToastOptions): Id;
  error(options: CustomToastOptions): Id;
  success(options: CustomToastOptions): Id;
  info(options: CustomToastOptions): Id;
  warning(options: CustomToastOptions): Id;
} & Omit<typeof toastify, "error" | "success" | "info" | "warning">;

const toast = ((options: CustomToastOptions): Id => {
  const { render, ...toastOptions } = options;
  return toastify(render ?? ((props) => <Toast data={props.data} />), {
    ...toastOptions,
    icon: <Icons.InfoIndicator role="status" className="text-gray-400" />,
  });
}) as ToastService;

Object.assign(toast, toastify);

toast.error = (options: CustomToastOptions): Id => {
  const { render, ...toastOptions } = options;
  return toastify.error(render ?? ((props) => <Toast data={props.data} />), {
    ...toastOptions,
    icon: <Icons.ExclamationCircle role="alert" className="size-6 text-critical-500" />,
  });
};

toast.info = (options: CustomToastOptions): Id => {
  const { render, ...toastOptions } = options;
  return toastify.info(render ?? ((props) => <Toast data={props.data} />), {
    ...toastOptions,
    icon: <Icons.InfoIndicator role="status" className="text-blue-500" />,
  });
};

toast.success = (options: CustomToastOptions): Id => {
  const { render, ...toastOptions } = options;
  return toastify.success(render ?? ((props) => <Toast data={props.data} />), {
    ...toastOptions,
    icon: <Icons.SuccessIndicator role="status" className="text-success-600" />,
  });
};

toast.warning = (options: CustomToastOptions): Id => {
  const { render, ...toastOptions } = options;
  return toastify.warning(render ?? ((props) => <Toast data={props.data} />), {
    ...toastOptions,
    icon: <Icons.WarningIndicator className="text-warning-500" role="alert" />,
  });
};

export default toast;
